import {
  isKey,
  isRecord
} from "./chunk-IPNO7PON.js";
import {
  PriceType
} from "./chunk-RCTGXDEU.js";
import {
  BusinessArea,
  Currency,
  Language,
  MeasurementSystem,
  PropertyMarketingType
} from "./chunk-VXLCCQ5A.js";

// src/utils/filters.ts
import { isEmpty } from "es-toolkit/compat";
import { isEmptyObject, mergeWith, omitBy } from "es-toolkit";
var DEFAULT_FILTERS = {
  businessArea: [BusinessArea.residential],
  propertyMarketingType: [PropertyMarketingType.sale]
};
var DEFAULT_OPTIONS = {
  language: Language.en,
  measurementSystem: MeasurementSystem.metric,
  currency: Currency.EUR
};
function getCurrencySuffixForFilterKey(currency) {
  return currency[0] + currency.slice(1).toLowerCase();
}
function mapPriceFilter(filter, propertyMarketingType, currency, priceType) {
  const priceTypePrefix = priceType ?? (propertyMarketingType === PropertyMarketingType.rent ? PriceType.RENT_ANY : PriceType.SALES_PRICE);
  const currencySuffix = getCurrencySuffixForFilterKey(currency);
  const priceDtoKey = `${priceTypePrefix}${currencySuffix}`;
  return {
    [priceDtoKey]: {
      ...filter
    }
  };
}
function mapSurfaceFilter(filter, name, measurementSystem) {
  const unitSuffix = measurementSystem === MeasurementSystem.metric ? "Sqmt" : "Sqft";
  const surfaceDtoKey = `${name}${unitSuffix}`;
  return {
    [surfaceDtoKey]: {
      ...filter
    }
  };
}
function mapSearchListingsFiltersToSearchFilterRequest(filters, options, priceType) {
  const { filters: refinedFilters, options: refinedOptions } = refineFiltersAndOptions(filters, options);
  const { price, plotSurface, livingSurface, totalSurface, ...restFilters } = refinedFilters;
  for (const key in restFilters) {
    if (isKey(restFilters, key) && (restFilters[key] === false || isRecord(restFilters[key]) && isEmptyObject(restFilters[key]))) {
      delete restFilters[key];
    }
  }
  return {
    ...restFilters,
    ...price ? Object.fromEntries(
      refinedFilters.propertyMarketingType.flatMap(
        (mt) => Object.entries(mapPriceFilter(price, mt, refinedOptions.currency, priceType))
      )
    ) : {},
    ...plotSurface && mapSurfaceFilter(plotSurface, "plotSurface", refinedOptions.measurementSystem),
    ...livingSurface && mapSurfaceFilter(livingSurface, "livingSurface", refinedOptions.measurementSystem),
    ...totalSurface && mapSurfaceFilter(totalSurface, "totalSurface", refinedOptions.measurementSystem)
  };
}
var PRICE_TYPE_KEYS_ON_LISTING = ["salesPrice", "rentTotal", "rentNet", "rentNetPerSquareMeter"];
function getSimilarListingsPriceInfo(property) {
  if (!property.basePrice) return;
  const matchingField = PRICE_TYPE_KEYS_ON_LISTING.find((key) => property.basePrice[key] !== void 0);
  if (!matchingField) return;
  const priceValue = property.basePrice[matchingField];
  if (!priceValue?.min || !priceValue.max) return;
  return {
    rangeFilter: { min: priceValue.min * 0.8, max: priceValue.max * 1.2 },
    priceType: matchingField
  };
}
var cleanEmptyObjects = (o) => {
  if (!isRecord(o)) return o;
  return Object.fromEntries(
    Object.entries(o).map(([k, v]) => [k, isRecord(v) && isEmptyObject(v) ? void 0 : cleanEmptyObjects(v)])
  );
};
var deleteEmptyObjects = (o) => {
  if (!isRecord(o)) return o;
  for (const key of Object.keys(o)) {
    const value = o[key];
    if (value === void 0 || isRecord(value) && isEmptyObject(value)) {
      delete o[key];
    }
  }
  return o;
};
function mergeState(state, refinement) {
  const _refinement = cleanEmptyObjects(refinement);
  const mergedObject = mergeWith(structuredClone(state), _refinement, (targetValue, srcValue, key, target) => {
    if (Array.isArray(srcValue)) {
      return srcValue;
    }
    if (targetValue !== srcValue && srcValue === void 0 && isRecord(target)) {
      const targetRecord = target;
      targetRecord[key] = void 0;
    }
  });
  return omitBy(mergedObject, (value) => value === void 0 || value === false);
}
function refineFiltersAndOptions(filters, options) {
  const refinedFilters = {
    ...DEFAULT_FILTERS,
    ...filters
  };
  const refinedOptions = {
    ...DEFAULT_OPTIONS,
    ...options
  };
  validateWithDefaults(refinedFilters, DEFAULT_FILTERS);
  validateWithDefaults(refinedOptions, DEFAULT_OPTIONS);
  return {
    filters: refinedFilters,
    options: refinedOptions
  };
}
function validateWithDefaults(data, defaults) {
  for (const key of Object.keys(defaults)) {
    if (!isKey(data, key)) continue;
    const defaultValue = defaults[key];
    const value = data[key];
    if (isEmpty(value) || Array.isArray(value) && value.some((item) => typeof item === "string" ? isEmpty(item.trim()) : isEmpty(item))) {
      data[key] = defaultValue;
    }
  }
}

export {
  DEFAULT_FILTERS,
  DEFAULT_OPTIONS,
  mapSearchListingsFiltersToSearchFilterRequest,
  getSimilarListingsPriceInfo,
  deleteEmptyObjects,
  mergeState,
  refineFiltersAndOptions
};
